/**
 * Audit page section 3: implementation. Up to 6 weeks from kickoff, tracked as actions
 * with an owner, due date and status. Longer-lead items continue into the retainer.
 */
import type { Lever1Summary, Lever2ParcelResult } from "@ryoma/engine";
import type { AuditRow, Db, Lever2RunSummary, UploadRow } from "../data/db.ts";
import {
  ACTION_STATUS_LABEL,
  IMPLEMENTATION_WEEKS,
  defaultImplementationEnd,
  implementationProgress,
  suggestedActions,
  type ActionStatus,
  type ImplementationAction,
} from "../lib/implementation.ts";
import { registerAuditSection } from "./audit.ts";
import { action, date, h, mount, notice, num, stat } from "./dom.ts";

const today = () => new Date().toISOString().slice(0, 10);
const LEVER_OPTIONS: [string, string][] = [["", "All levers"], ["1", "Lever 1"], ["2", "Lever 2"], ["3", "Lever 3"], ["4", "Lever 4"], ["5", "Lever 5"]];

function actionRow(db: Db, auditId: string, a: ImplementationAction, refresh: () => void): HTMLElement {
  const title = h("input", { type: "text", value: a.title, "aria-label": "Action", maxlength: 300 });
  const owner = h("input", { type: "text", value: a.owner, "aria-label": "Owner", placeholder: "Who", maxlength: 120, class: "input-short" });
  const due = h("input", { type: "date", value: a.due ?? "", "aria-label": "Due" });
  const lever = h("select", { "aria-label": "Lever" }, LEVER_OPTIONS.map(([v, t]) => h("option", { value: v, selected: String(a.lever ?? "") === v }, t)));
  const status = h("select", { "aria-label": "Status" }, (Object.keys(ACTION_STATUS_LABEL) as ActionStatus[]).map((k) => h("option", { value: k, selected: a.status === k }, ACTION_STATUS_LABEL[k])));
  const overdue = a.status !== "done" && a.due !== null && a.due < today();
  const row = h("div", { class: `action-row status-${a.status}${overdue ? " overdue" : ""}`, "data-action": a.title },
    lever, title, owner, due, status,
    action("Save", async (_b, st) => {
      if (!title.value.trim()) throw new Error("An action needs a title");
      await db.saveAction(auditId, {
        ...a,
        title: title.value,
        owner: owner.value.trim(),
        due: due.value || null,
        lever: lever.value ? (Number(lever.value) as ImplementationAction["lever"]) : null,
        status: status.value as ActionStatus,
      });
      st.textContent = "Saved";
      refresh();
    }),
    a.id ? h("button", { type: "button", class: "btn btn-quiet", "aria-label": `Delete ${a.title}`, onClick: async () => {
      if (!confirm(`Delete "${a.title}"?`)) return;
      await db.deleteAction(a.id!);
      refresh();
    } }, "Delete") : "",
  );
  return row;
}

async function implementationSection({ db, audit, upload, refresh }: { db: Db; audit: AuditRow; upload: UploadRow | null; refresh: () => void }): Promise<HTMLElement> {
  const box = h("div", { class: "card" }, h("h2", null, "4. Implementation"));
  if (!upload) {
    box.append(notice("info", "Upload client data, run the levers and send the report first."));
    return box;
  }
  const actions = await db.listActions(audit.id);
  const start = h("input", { type: "date", value: audit.implementation_starts ?? "", "aria-label": "Implementation starts" });
  const end = h("input", { type: "date", value: audit.implementation_ends ?? "", "aria-label": "Implementation ends" });
  start.addEventListener("change", () => {
    if (start.value && !audit.implementation_ends) end.value = defaultImplementationEnd(start.value);
  });
  const saveDates = action("Save dates", async () => {
    if (!start.value) throw new Error("Choose the kickoff date");
    if (end.value && end.value < start.value) throw new Error("Implementation cannot end before it starts");
    await db.updateAudit(audit.id, { implementation_starts: start.value, implementation_ends: end.value || defaultImplementationEnd(start.value) });
    refresh();
  }, { primary: !audit.implementation_starts });

  const p = implementationProgress(actions, audit.implementation_starts, audit.implementation_ends, today());
  const weeks = audit.implementation_starts && audit.implementation_ends
    ? (Date.parse(audit.implementation_ends) - Date.parse(audit.implementation_starts)) / (7 * 864e5)
    : null;

  const suggest = action("Add suggested actions", async () => {
    const begin = audit.implementation_starts ?? today();
    const runs = await db.latestLeverRuns(audit.id);
    const manual = await db.manualLevers(audit.id);
    const s1 = runs[1]?.dataset_hash === upload.dataset_hash ? (runs[1].summary as Lever1Summary) : null;
    const s2 = runs[2]?.dataset_hash === upload.dataset_hash ? (runs[2].summary as Lever2RunSummary) : null;
    const topCode = s2 ? Object.entries(s2.byFormat).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null : null;
    const names = new Map(((runs[2]?.results ?? []) as Lever2ParcelResult[]).filter((r) => r.proposed).map((r) => [r.proposed!.formatCode, r.proposed!.formatName]));
    const list = suggestedActions({
      start: begin,
      lever1: s1 ? { overchargedCount: s1.overchargedCount, nearBandCount: s1.nearBandCount } : null,
      lever2: s2 ? { savingCount: s2.savingCount, topFormat: topCode ? (names.get(topCode) ?? topCode) : null } : null,
      manual,
    });
    const existing = new Set(actions.map((a) => a.title));
    for (const a of list) if (!existing.has(a.title)) await db.saveAction(audit.id, a);
    refresh();
  });

  const newTitle = h("input", { type: "text", placeholder: "New action", "aria-label": "New action", maxlength: 300 });
  const add = action("Add", async () => {
    if (!newTitle.value.trim()) throw new Error("Type the action first");
    await db.saveAction(audit.id, { lever: null, title: newTitle.value, owner: "", due: null, status: "todo", note: "" });
    refresh();
  });

  box.append(
    h("p", { class: "muted small" }, `Implementation takes up to ${IMPLEMENTATION_WEEKS} weeks from kickoff. Longer-lead items, such as custom packaging, continue into the retainer.`),
    h("div", { class: "inline-form first" },
      h("label", null, "Kickoff ", start), h("label", null, "Ends ", end), saveDates,
      h("span", { class: "muted small" }, "The end defaults to 6 weeks after kickoff."),
    ),
    weeks !== null && weeks > IMPLEMENTATION_WEEKS ? notice("warn", `This plan runs ${Math.ceil(weeks)} weeks, longer than the ${IMPLEMENTATION_WEEKS} weeks we offer. Move longer items into the retainer.`) : "",
    audit.implementation_starts
      ? h("div", { class: "stats" },
          stat("Week", p.week === null ? "Not started" : `${Math.min(p.week, 99)} of ${IMPLEMENTATION_WEEKS}`, `${date(audit.implementation_starts)} to ${date(audit.implementation_ends)}`),
          p.week === null
            ? stat("Days to kickoff", num(Math.max(0, Math.round((Date.parse(audit.implementation_starts) - Date.parse(today())) / 864e5))))
            : stat("Days left", p.daysLeft === null ? "–" : num(Math.max(p.daysLeft, 0)), undefined, p.overrun ? "bad" : p.daysLeft !== null && p.daysLeft <= 7 ? "warn" : undefined),
          stat("Actions done", `${num(p.done)} of ${num(p.total)}`, undefined, p.total > 0 && p.done === p.total ? "good" : undefined),
          stat("Overdue or blocked", num(p.overdue + p.blocked), `${num(p.overdue)} overdue, ${num(p.blocked)} blocked`, p.overdue + p.blocked > 0 ? "warn" : undefined),
        )
      : "",
    p.overrun ? notice("error", "The end date has passed with actions still open. Finish them, move them into the retainer, or agree a new date with the client.") : "",
    h("h4", null, "Action plan"),
    actions.length === 0 ? notice("info", "No actions yet. Add the suggested ones from the lever results, then edit them.") : "",
    h("div", { class: "actions-list" }, actions.map((a) => actionRow(db, audit.id, a, refresh))),
    h("div", { class: "inline-form" }, newTitle, add, suggest),
  );
  return box;
}

registerAuditSection(implementationSection);
